import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Banknote, Landmark, ReceiptText } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { PageHeader, Loading, StatCard, inr } from "../../components/shared";
import FinanceSales from "./finance/FinanceSales";
import FinancePayments from "./finance/FinancePayments";
import FinanceJournal from "./finance/FinanceJournal";
import FinanceLedger from "./finance/FinanceLedger";
import FinanceMasters from "./finance/FinanceMasters";
import FinanceReports from "./finance/FinanceReports";
import { DataTable, FinanceCard, NativeSelect, money, todayIso } from "./finance/financeUi";

const TABS = ["sales", "receipts", "payments", "journals", "ledger", "masters", "accounts", "reports"];
const BASE = "/admin/company-finance";
const quiet = (promise, set) => promise.then((r) => set(r.data)).catch(() => {});

export default function AdminCompanyFinance() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "sales";
  const setTab = (v) => setSearchParams(v === "sales" ? {} : { tab: v });
  const [data, setData] = useState({ dashboard: null, accounts: [], parties: [], items: [], units: [], invoices: [], receipts: [], payments: [], vouchers: [], trial: null, pnl: null, balance: null });
  const patch = (key) => (value) => setData((d) => ({ ...d, [key]: value }));

  const load = useCallback(() => {
    quiet(api.get(`${BASE}/dashboard`), patch("dashboard"));
    quiet(api.get(`${BASE}/accounts`), patch("accounts"));
    quiet(api.get(`${BASE}/parties`), patch("parties"));
    quiet(api.get(`${BASE}/items`), patch("items"));
    quiet(api.get(`${BASE}/units`), patch("units"));
    quiet(api.get(`${BASE}/invoices`), patch("invoices"));
    quiet(api.get(`${BASE}/receipts`), patch("receipts"));
    quiet(api.get(`${BASE}/payments`), patch("payments"));
    quiet(api.get(`${BASE}/vouchers`), patch("vouchers"));
    quiet(api.get(`${BASE}/reports/trial-balance`), patch("trial"));
    quiet(api.get(`${BASE}/reports/profit-loss`), patch("pnl"));
    quiet(api.get(`${BASE}/reports/balance-sheet`), patch("balance"));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data.dashboard) return <Loading />;
  const { dashboard } = data;
  return (
    <div data-testid="company-finance-page">
      <PageHeader title="Company Finance" subtitle="MySocietyMitra / Shivkrupa company books — separate from every Society ERP ledger." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={ReceiptText} label="Today’s collections" testid="company-finance-today" tone="secondary" value={inr(dashboard.today_collections)} />
        <StatCard icon={Banknote} label="This month revenue" testid="company-finance-revenue" tone="primary" value={inr(dashboard.month_revenue)} />
        <StatCard icon={Banknote} label="Receivables" testid="company-finance-receivables" tone="rose" value={inr(dashboard.receivables)} />
        <StatCard icon={Landmark} label="This month expenses" testid="company-finance-expenses" tone="amber" value={inr(dashboard.month_expenses)} />
      </div>
      <Tabs onValueChange={setTab} value={tab}>
        <TabsList className="mb-5 h-auto flex-wrap">
          <TabsTrigger data-testid="company-finance-sales-tab" value="sales">Sales Invoices</TabsTrigger>
          <TabsTrigger data-testid="company-finance-receipts-tab" value="receipts">Receipts</TabsTrigger>
          <TabsTrigger data-testid="company-finance-payments-tab" value="payments">Payments</TabsTrigger>
          <TabsTrigger data-testid="company-finance-journals-tab" value="journals">Journals</TabsTrigger>
          <TabsTrigger data-testid="company-finance-ledger-tab" value="ledger">Ledger View</TabsTrigger>
          <TabsTrigger data-testid="company-finance-masters-tab" value="masters">Masters</TabsTrigger>
          <TabsTrigger data-testid="company-finance-accounts-tab" value="accounts">Chart of Accounts</TabsTrigger>
          <TabsTrigger data-testid="company-finance-reports-tab" value="reports">Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="sales"><FinanceSales accounts={data.accounts} invoices={data.invoices} items={data.items} parties={data.parties} reload={load} /></TabsContent>
        <TabsContent value="receipts"><Receipts invoices={data.invoices} receipts={data.receipts} reload={load} /></TabsContent>
        <TabsContent value="payments"><FinancePayments accounts={data.accounts} parties={data.parties} payments={data.payments} reload={load} /></TabsContent>
        <TabsContent value="journals"><FinanceJournal accounts={data.accounts} reload={load} vouchers={data.vouchers} /></TabsContent>
        <TabsContent value="ledger"><FinanceLedger accounts={data.accounts} parties={data.parties} /></TabsContent>
        <TabsContent value="masters"><FinanceMasters accounts={data.accounts} items={data.items} parties={data.parties} reload={load} units={data.units} /></TabsContent>
        <TabsContent value="accounts"><Accounts accounts={data.accounts} /></TabsContent>
        <TabsContent value="reports"><FinanceReports balance={data.balance} pnl={data.pnl} trial={data.trial} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Receipts({ invoices, receipts, reload }) {
  const open = invoices.filter((i) => Number(i.outstanding) > 0);
  const [form, setForm] = useState({ invoice_id: "", amount: "", mode: "Bank Transfer", reference: "", date: todayIso() });
  const post = async () => {
    try {
      await api.post(`${BASE}/receipts`, { ...form, amount: Number(form.amount) });
      toast.success("Company receipt posted");
      setForm({ ...form, invoice_id: "", amount: "", reference: "" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post receipt");
    }
  };
  return (
    <div className="grid gap-5 lg:grid-cols-[380px_1fr]">
      <FinanceCard testid="receipt-form" title="Record receipt">
        <Input data-testid="company-receipt-date-input" onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" value={form.date} />
        <NativeSelect data-testid="company-receipt-invoice-select" onChange={(e) => setForm({ ...form, invoice_id: e.target.value })} value={form.invoice_id}>
          <option value="">Open invoice…</option>
          {open.map((i) => <option key={i.invoice_id} value={i.invoice_id}>{i.invoice_number} · {i.party?.name || "—"} · due {money(i.outstanding)}</option>)}
        </NativeSelect>
        <Input data-testid="company-receipt-invoice-input" onChange={(e) => setForm({ ...form, invoice_id: e.target.value })} placeholder="…or paste company invoice ID" value={form.invoice_id} />
        <Input data-testid="company-receipt-amount-input" onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Receipt amount" type="number" value={form.amount} />
        <NativeSelect data-testid="company-receipt-mode-select" onChange={(e) => setForm({ ...form, mode: e.target.value })} value={form.mode}>
          <option>Bank Transfer</option><option>UPI</option><option>Cheque</option><option>Cash</option>
        </NativeSelect>
        <Input data-testid="company-receipt-reference-input" onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Reference / UTR" value={form.reference} />
        <Button data-testid="company-receipt-save-button" onClick={post}><ReceiptText className="w-4 h-4" />Post Receipt</Button>
      </FinanceCard>
      <DataTable
        columns={[
          ["Receipt", (r) => <span className="font-mono text-xs">{r.receipt_number}</span>],
          ["Date", (r) => r.date],
          ["Party", (r) => r.party?.name || "—"],
          ["Invoice", (r) => <span className="font-mono text-[11px]">{r.invoice_id || "—"}</span>],
          ["Mode", (r) => r.mode],
          ["Amount", (r) => money(r.amount), "right"],
        ]}
        rowKey={(r) => r.receipt_id}
        rows={receipts}
        testid="receipt-table"
      />
    </div>
  );
}

function Accounts({ accounts }) {
  return (
    <DataTable
      columns={[
        ["Code", (a) => <span className="font-mono">{a.code}</span>],
        ["Account", (a) => a.name],
        ["Type", (a) => <span className="capitalize">{a.type}</span>],
        ["Opening", (a) => money(a.opening_balance), "right"],
      ]}
      rowKey={(a) => a.account_id}
      rows={accounts}
      testid="company-accounts-table"
    />
  );
}
